package com.example.event_management_system.ui.screens.events

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Search
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.TextButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.koin.getScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.example.event_management_system.data.models.EventStatus
import com.example.event_management_system.ui.components.common.EmptyState
import com.example.event_management_system.ui.components.common.ErrorMessage
import com.example.event_management_system.ui.components.common.LoadingIndicator
import com.example.event_management_system.ui.components.event.EventListItem
import com.example.event_management_system.ui.theme.Dimens
import event_management_system.composeapp.generated.resources.Res
import event_management_system.composeapp.generated.resources.*
import org.jetbrains.compose.resources.stringResource

data class EventListScreen(val initialLocationId: String? = null) : Screen {

    @OptIn(ExperimentalMaterial3Api::class)
    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val screenModel = getScreenModel<EventListScreenModel>()
        val state by screenModel.state.collectAsState()
        LaunchedEffect(initialLocationId) {
            if (initialLocationId != null) screenModel.preSelectLocation(initialLocationId)
        }

        Scaffold(
            topBar = {
                TopAppBar(
                    title = { Text(stringResource(Res.string.screen_title_events)) },
                    navigationIcon = {
                        IconButton(onClick = { navigator.pop() }) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                        }
                    },
                    colors = TopAppBarDefaults.topAppBarColors(
                        containerColor = MaterialTheme.colorScheme.surface,
                        titleContentColor = MaterialTheme.colorScheme.onSurface
                    )
                )
            },
            floatingActionButton = {
                FloatingActionButton(
                    onClick = { navigator.push(EventFormScreen(initialLocationId = state.selectedLocationId)) },
                    containerColor = MaterialTheme.colorScheme.primary,
                    contentColor = MaterialTheme.colorScheme.onPrimary
                ) {
                    Icon(Icons.Default.Add, contentDescription = "New Event")
                }
            }
        ) { padding ->
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(padding)
            ) {
                // Search bar
                OutlinedTextField(
                    value = state.searchQuery,
                    onValueChange = screenModel::updateSearchQuery,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = Dimens.paddingMedium, vertical = Dimens.spacingSmall),
                    placeholder = { Text(stringResource(Res.string.hint_search_events)) },
                    leadingIcon = { Icon(Icons.Default.Search, contentDescription = null) },
                    trailingIcon = {
                        if (state.searchQuery.isNotEmpty()) {
                            IconButton(onClick = { screenModel.updateSearchQuery("") }) {
                                Icon(Icons.Default.Close, contentDescription = "Clear")
                            }
                        }
                    },
                    singleLine = true
                )

                // Filters row
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = Dimens.paddingMedium, vertical = Dimens.spacingSmall),
                    horizontalArrangement = Arrangement.spacedBy(Dimens.spacingSmall)
                ) {
                        // Status filter chips
                    FilterChip(
                        selected = state.selectedStatus == null,
                        onClick = { screenModel.selectStatus(null) },
                        label = { Text(stringResource(Res.string.filter_all)) }
                    )
                    FilterChip(
                        selected = state.selectedStatus == EventStatus.PENDING,
                        onClick = { screenModel.selectStatus(EventStatus.PENDING) },
                        label = { Text(stringResource(Res.string.filter_pending)) }
                    )
                    FilterChip(
                        selected = state.selectedStatus == EventStatus.CONFIRMED,
                        onClick = { screenModel.selectStatus(EventStatus.CONFIRMED) },
                        label = { Text(stringResource(Res.string.filter_confirmed)) }
                    )
                }

                when {
                    state.isLoading -> {
                        LoadingIndicator()
                    }
                    state.error != null -> {
                        ErrorMessage(
                            message = state.error!!,
                            onRetry = screenModel::refresh
                        )
                    }
                    state.filteredEvents.isEmpty() -> {
                        EmptyState(
                            title = stringResource(Res.string.empty_title_no_events),
                            message = if (state.searchQuery.isNotEmpty() || state.selectedStatus != null) {
                                stringResource(Res.string.empty_msg_adjust_filters)
                            } else {
                                stringResource(Res.string.empty_msg_no_events)
                            },
                            actionText = stringResource(Res.string.action_create_event),
                            onAction = { navigator.push(EventFormScreen(initialLocationId = state.selectedLocationId)) }
                        )
                    }
                    else -> {
                        LazyColumn(
                            modifier = Modifier.fillMaxSize(),
                            contentPadding = PaddingValues(Dimens.paddingMedium),
                            verticalArrangement = Arrangement.spacedBy(Dimens.spacingSmall)
                        ) {
                            items(state.filteredEvents) { event ->
                                EventListItem(
                                    event = event,
                                    onClick = { navigator.push(EventDetailScreen(event.id)) }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}
